import { useLockedPositionAPY, useDialogPortal } from "@/hooks";
import { memo, useCallback, useMemo, type MouseEvent } from "react";
import { PositionTableRow } from "../row/PositionTableRow";
import type { PositionStatus } from "../row/position-table-row.utils";
import type { UnwindingPositionStore } from "@/hooks/stores/useUnwindingStore";
import type { LockMoreFormDialogProps } from "@/components";
import { usePortfolioStore } from "@/zustand/PortfolioStore";
import { TOKEN_IDS } from "@/types/token.types";
import { formatDisplayCurrency } from "@/utils/format-currency";

type UnwindingPositionRowStore = UnwindingPositionStore & {
    iUSDBalance?: number | string;
};

export interface UnlockPositionRowProps {
    position: UnwindingPositionRowStore;
    index: number;
    isMenuOpen: boolean;
    menuIndex: number | null;
    onMenuToggle: (event: MouseEvent<HTMLElement>, index: number, status?: PositionStatus) => void;
    status?: PositionStatus;
}

export const UnlockPositionRow = memo(function UnlockPositionRow({
    position,
    index,
    isMenuOpen,
    menuIndex,
    onMenuToggle,
    status,
}: UnlockPositionRowProps) {
    const { openDialog } = useDialogPortal();
    const { selectedPosition, setSelectedPosition } = usePortfolioStore();
    const rowKey = `unwinding-${index}`;
    const isSelected = selectedPosition?.key === rowKey;
    const handleSelect = useCallback(() => {
        setSelectedPosition({ type: "unwinding", key: rowKey, position });
    }, [position, rowKey, setSelectedPosition]);

    const lockDurationWeeks = useMemo(() => Number(position.lockDuration.split("w")[0]), [position.lockDuration]);
    const durationText = useMemo(() => {
        if (!Number.isFinite(lockDurationWeeks)) {
            return "N/A";
        }
        return `${lockDurationWeeks}w`;
    }, [lockDurationWeeks]);

    const handleAction = useCallback(() => {
        openDialog({
            dialogType: "LOCK_MORE_FORM",
            dialogProps: {
                existingDuration: lockDurationWeeks,
                initialTokenId: TOKEN_IDS.IUSD,
            } satisfies LockMoreFormDialogProps,
        });
    }, [openDialog, lockDurationWeeks]);

    const { apy, isAPYLoading } = useLockedPositionAPY(lockDurationWeeks);

    const {
        principal,
        rewards,
        apy: apyText,
    } = useMemo(() => {
        const rawBalance = position.lockedBalance;
        const iUsdValue = Number(rawBalance);
        const principalText = Number.isFinite(iUsdValue) ? `${formatDisplayCurrency(iUsdValue)}` : "0.00";

        return {
            principal: `${principalText} iUSD`,
            rewards: position.rewardsEarned,
            apy: isAPYLoading ? "—" : apy ? `${apy}%` : "N/A",
        };
    }, [position.rewardsEarned, apy, isAPYLoading]);

    return (
        <PositionTableRow
            durationText={durationText}
            principal={principal}
            rewards={rewards}
            apy={apyText}
            isActionDisabled={false}
            handleAction={handleAction}
            actionButtonText="Lock More"
            isMenuOpen={isMenuOpen}
            menuIndex={menuIndex}
            index={index}
            onMenuToggle={(event) => onMenuToggle(event, index, status)}
            status={status}
            isSelected={isSelected}
            onSelect={handleSelect}
        />
    );
});

UnlockPositionRow.displayName = "UnlockPositionRow";
